import React, { useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import './style.css'

const PHONE = 'tel:+18559147363'
const PHONE_DISPLAY = '+1 855 914 7363'

const META_DESCRIPTION =
  'Live demo of the UHG Voice AI Agent. Call one number and hear it handle three member scenarios: a declined UCard, an unexpected medical bill, and finding a new PCP.'

const HUB_CARDS = [
  {
    id: '1',
    num: '01',
    steps: '3 steps',
    title: 'UCard declined at checkout',
    objective:
      'Your UCard keeps declining at the store. The agent verifies your account and works the issue with you.',
  },
  {
    id: '2',
    num: '02',
    steps: '4 steps',
    title: 'Unexpected medical bill',
    objective:
      "A doctor's bill you did not expect. The agent checks it against what your DSNP plan covers.",
  },
  {
    id: '3',
    num: '03',
    steps: '3 steps',
    title: 'Find and assign a new PCP',
    objective: 'The agent finds a primary care provider near you and sets them as your PCP.',
  },
]

const SCENARIOS = {
  1: {
    title: 'UCard declined at checkout',
    stepLabel: 'Scenario 01 of 03 · DSNP',
    situation: 'Your UCard was declined at Walmart while buying groceries, and it keeps happening.',
    steps: [
      {
        num: '01',
        text: 'Start the call and describe the problem.',
        hint: 'Try saying',
        says: ["My UCard didn't work at Walmart. It keeps declining."],
      },
      {
        num: '02',
        text: 'Complete authentication when the agent asks.',
        mini: ['Name', 'Member ID', 'Date of birth', 'Address'],
      },
      {
        num: '03',
        text: 'Explain the issue. Confirm that:',
        mini: ['The card was declined', 'It happened at Walmart', 'You were buying groceries'],
      },
    ],
    nextId: '2',
    nextLabel: 'Next · Unexpected medical bill',
  },
  2: {
    title: 'Unexpected medical bill',
    stepLabel: 'Scenario 02 of 03 · DSNP',
    situation:
      'You received a $45 bill from your doctor, but you believe your DSNP plan should have covered it.',
    steps: [
      {
        num: '01',
        text: 'Start the call and describe the problem.',
        hint: 'Try saying',
        says: [
          'I received a $45 bill from my doctor, but I thought my DSNP plan covered everything.',
        ],
      },
      {
        num: '02',
        text: 'Complete authentication when the agent asks.',
        mini: ['Name', 'Member ID', 'Date of birth', 'Address'],
      },
      {
        num: '03',
        text: 'Explain the bill. Confirm:',
        mini: ['The bill amount', 'The date of service', "The provider or doctor's name"],
      },
      {
        num: '04',
        text: 'Answer any follow-up questions.',
        mini: [
          'Provider phone number',
          "Whether you've already paid the bill",
          'Any additional claim information',
        ],
      },
    ],
    nextId: '3',
    nextLabel: 'Next · Find and assign a new PCP',
  },
  3: {
    title: 'Find and assign a new PCP',
    stepLabel: 'Scenario 03 of 03 · CAID',
    situation: 'You need a new primary care provider, and you want to set them as your PCP.',
    steps: [
      {
        num: '01',
        text: 'Start the call and make the request.',
        hint: 'Try saying',
        says: ['I need a new doctor. Can you help me find one and assign them as my PCP?'],
      },
      {
        num: '02',
        text: 'Complete authentication when the agent asks.',
        mini: ['Name', 'Member ID', 'Date of birth', 'Address'],
      },
      {
        num: '03',
        text: 'Share your preferences.',
        mini: [
          'ZIP code',
          'Preferred provider gender, if any',
          'Preferred language, if any',
          'Preferred distance',
          'Any specific doctor or clinic',
        ],
      },
    ],
    nextId: null,
    nextLabel: 'All scenarios',
  },
}

function IbexLogo() {
  return (
    <span className="logo" aria-label="ibex">
      <svg viewBox="0 0 76 27" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="ibex">
        <path d="M0 3.45398C0 1.48665 1.58018 0 3.58376 0C5.58735 0 7.13225 1.48665 7.13225 3.45398C7.13225 5.42131 5.58735 6.97733 3.58376 6.97733C1.58018 6.97733 0 5.49316 0 3.45398Z" fill="currentColor" />
        <path d="M0.491444 26.777V7.72561H6.71137V26.777H0.491444Z" fill="currentColor" />
        <path d="M29.6802 16.7919C29.6802 22.6121 25.5471 26.7772 20.0832 26.7772C17.631 26.7772 15.8795 25.848 14.6194 24.5745V26.802H8.66406V0.0200195H14.8285V8.76646C16.0534 7.62918 17.7696 6.80409 20.0479 6.80409C25.5471 6.80409 29.6802 11.0063 29.6802 16.7894V16.7919ZM23.3772 16.7572C23.3772 14.3463 21.555 12.3493 18.9642 12.3493C16.3734 12.3493 14.5866 14.3463 14.5866 16.7572C14.5866 19.168 16.4087 21.1998 18.9642 21.1998C21.5197 21.1998 23.3772 19.2374 23.3772 16.7572Z" fill="currentColor" />
        <path d="M50.3678 18.7591H36.4587C37.0938 20.5629 38.6488 21.7795 41.2245 21.7795C43.2709 21.7795 45.6021 21.0163 47.1193 20.0104L49.5563 24.1408C47.5805 25.5283 44.6142 26.7796 40.9422 26.7796C33.6688 26.7796 30.1758 22.0248 30.1758 16.7843C30.1758 11.0583 34.3417 6.71973 40.5894 6.71973C46.2019 6.71973 50.5795 10.2257 50.5795 16.2987C50.5795 17.0618 50.509 17.8944 50.3678 18.7641V18.7591ZM36.3856 14.5593H44.6469C44.1177 12.7555 42.6711 11.6083 40.5868 11.6083C38.5026 11.6083 37.0207 12.649 36.3856 14.5593Z" fill="currentColor" />
        <path d="M62.3052 26.7769L59.2078 20.9046L55.8534 26.7769H48.4414L54.8579 16.6999L49.2529 6.7666H56.6649L59.2834 12.2053L62.3808 6.7666H69.571L63.5981 16.7346L69.8306 26.7769H62.3077H62.3052Z" fill="currentColor" />
        <path d="M72.6582 26.9977C74.5039 26.9977 76 25.5268 76 23.7122C76 21.8977 74.5039 20.4268 72.6582 20.4268C70.8126 20.4268 69.3164 21.8977 69.3164 23.7122C69.3164 25.5268 70.8126 26.9977 72.6582 26.9977Z" fill="currentColor" />
      </svg>
    </span>
  )
}

function WaveMark() {
  return (
    <img
      className="wavemark"
      src={`${process.env.PUBLIC_URL}/assets/images/healthcare-ai-voice-demo/wave-ix-logo.svg`}
      alt="Wave iX"
      width="109"
      height="16"
    />
  )
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  )
}

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="m15 18-6-6 6-6" />
    </svg>
  )
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

function CallButton() {
  return (
    <a className="call" href={PHONE}>
      <span className="call-ico" aria-hidden="true">
        <PhoneIcon />
      </span>
      <span className="call-txt">
        <span className="call-lbl">Tap to call the agent</span>
        <span className="call-num">{PHONE_DISPLAY}</span>
      </span>
    </a>
  )
}

function CallQuiet() {
  return (
    <a className="callquiet" href={PHONE}>
      <span className="callquiet-ico" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      </span>
      <span className="callquiet-lbl">One number for all three</span>
      <span className="callquiet-num">{PHONE_DISPLAY}</span>
    </a>
  )
}

function ScenarioRail({ stepLabel, title, onHome }) {
  return (
    <header className="rail rail--run">
      <div className="rail-top">
        <IbexLogo />
        <WaveMark />
      </div>

      <div className="backrow">
        <button className="back" type="button" onClick={onHome}>
          <BackIcon />
          All scenarios
        </button>
        <span className="stepof">{stepLabel}</span>
      </div>

      <div className="rail-hd">
        <h2 className="rail-title">{title}</h2>
      </div>

      <CallButton />
    </header>
  )
}

function ScenarioStep({ step }) {
  return (
    <li className="step">
      <span className="step-num">{step.num}</span>
      <p className="step-txt">{step.text}</p>
      {step.hint ? <p className="hint">{step.hint}</p> : null}
      {step.says ? (
        <div className="says">
          {step.says.map((line) => (
            <p key={line} className="say">
              {line}
            </p>
          ))}
        </div>
      ) : null}
      {step.mini ? (
        <ul className="mini">
          {step.mini.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
    </li>
  )
}

function ScenarioView({ id, currentView, onHome, onNavigate }) {
  const scenario = SCENARIOS[id]

  return (
    <section className={`view${currentView === String(id) ? ' on' : ''}`} id={`view-${id}`} data-view={id}>
      <ScenarioRail stepLabel={scenario.stepLabel} title={scenario.title} onHome={onHome} />
      <main className="body">
        <div className="run">
          <div className="objective">
            <span className="objective-lbl">Your situation</span>
            <p className="objective-txt">{scenario.situation}</p>
          </div>

          <ol className="steps">
            {scenario.steps.map((step) => (
              <ScenarioStep key={step.num} step={step} />
            ))}
          </ol>

          <div className="run-foot">
            <button
              className="work-cta"
              type="button"
              onClick={() => (scenario.nextId ? onNavigate(scenario.nextId) : onHome())}
            >
              {scenario.nextLabel}
              <span className="wc-ico" aria-hidden="true">
                {scenario.nextId ? <ArrowIcon /> : <CheckIcon />}
              </span>
            </button>
            {scenario.nextId ? (
              <button className="run-alt" type="button" onClick={onHome}>
                Back to all scenarios
              </button>
            ) : null}
          </div>
        </div>
      </main>
    </section>
  )
}

export default function HealthcareAiVoiceDemoPage() {
  const [currentView, setCurrentView] = useState('hub')
  const [minibarOn, setMinibarOn] = useState(false)

  const show = useCallback((id, push = true) => {
    setCurrentView(id)
    setMinibarOn(false)

    if (push) {
      const hash = id === 'hub' ? '#scenarios' : `#scenario-${id}`
      if (window.location.hash !== hash) {
        window.history.pushState({ v: id }, '', hash)
      }
    }

    requestAnimationFrame(() => {
      window.scrollTo(0, 0)
      const bodyEl = document.querySelector(`[data-view="${id}"] .body`)
      if (bodyEl instanceof HTMLElement) bodyEl.scrollTop = 0
    })
  }, [])

  const goHome = useCallback(() => show('hub'), [show])

  useEffect(() => {
    const fromHash = () => {
      const match = (window.location.hash || '').match(/^#scenario-([123])$/)
      show(match ? match[1] : 'hub', false)
    }

    fromHash()
    window.addEventListener('popstate', fromHash)
    return () => window.removeEventListener('popstate', fromHash)
  }, [show])

  useEffect(() => {
    if (currentView === 'hub') {
      setMinibarOn(false)
      return undefined
    }

    const isMobile = window.matchMedia('(max-width:1023px), (max-height:559px)')
    let ticking = false

    const onScroll = () => {
      if (!isMobile.matches || currentView === 'hub') {
        setMinibarOn(false)
        return
      }
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        ticking = false
        const rail = document.querySelector(`[data-view="${currentView}"] .rail`)
        const past = rail instanceof HTMLElement && window.scrollY > rail.offsetHeight - 40
        setMinibarOn(!!past)
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    isMobile.addEventListener('change', onScroll)
    onScroll()

    return () => {
      window.removeEventListener('scroll', onScroll)
      isMobile.removeEventListener('change', onScroll)
    }
  }, [currentView])

  const viewClass = (id) => `view${currentView === id ? ' on' : ''}`
  const minibarTitle = currentView !== 'hub' ? SCENARIOS[Number(currentView)]?.title : ''

  return (
    <>
      <Helmet>
        <title>UHG Voice AI Agent · Live Demo | ibex</title>
        <meta name="description" content={META_DESCRIPTION} />
        <link rel="canonical" href="https://www.ibex.co/healthcare-ai-voice-demo" />
        <meta property="og:title" content="UHG Voice AI Agent · Live Demo" />
        <meta property="og:description" content={META_DESCRIPTION} />
        <meta property="og:url" content="https://www.ibex.co/healthcare-ai-voice-demo" />
        <meta property="og:type" content="website" />
        <meta name="theme-color" content="#0F0F0E" />
      </Helmet>

      <div className={`healthcare-ai-voice-demo-page${currentView === 'hub' ? ' on-hub' : ''}`}>
        <div className={`minibar${minibarOn ? ' on' : ''}`} aria-hidden={minibarOn ? 'false' : 'true'}>
          <div className="minibar-row">
            <button className="minibar-back" type="button" onClick={goHome} aria-label="Back to all scenarios">
              <BackIcon />
            </button>
            <span className="minibar-ttl">{minibarTitle}</span>
          </div>
        </div>

        <div className="stage">
          <section className={viewClass('hub')} id="view-hub" data-view="hub">
            <header className="rail">
              <div className="rail-top">
                <IbexLogo />
                <WaveMark />
              </div>

              <div className="rail-hd">
                <h1 className="rail-title">
                  UHG Voice AI Agent <u>Live demo</u>
                </h1>
                <p className="rail-sub">
                  Pick a scenario, call the number, and talk to the agent the way you would talk to a person. Take them in any order.
                </p>
              </div>

              <CallQuiet />
            </header>

            <main className="body">
              <div className="hub">
                <p className="hub-lbl">Choose a scenario</p>
                <div className="cards">
                  {HUB_CARDS.map((card) => (
                    <button key={card.id} className="card" type="button" onClick={() => show(card.id)}>
                      <span className="card-top">
                        <span className="card-num">{card.num}</span>
                        <span className="card-steps">
                          <span>{card.steps}</span>
                        </span>
                      </span>
                      <span className="card-ttl">{card.title}</span>
                      <span className="card-obj">{card.objective}</span>
                      <span className="card-foot">
                        <span className="card-go">
                          Start
                          <ArrowIcon />
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <section className="note">
                <p className="note-kick">One number, three scenarios</p>
                <h2 className="note-h">
                  It answers in <u>plain language</u>.
                </h2>
                <p className="note-p">
                  The same agent handles all three. It verifies the account once, carries that context between journeys, and explains every decision it makes out loud.
                </p>
              </section>
            </main>
          </section>

          {['1', '2', '3'].map((id) => (
            <ScenarioView
              key={id}
              id={Number(id)}
              currentView={currentView}
              onHome={goHome}
              onNavigate={show}
            />
          ))}
        </div>

        <div className="callbar">
          <CallButton />
        </div>
      </div>
    </>
  )
}
